/**
 * The app's ready room shapes (`NewProjectsStorage`, «Форма комнаты»): a new project can start from one, and the room
 * tool adds one beside the plan (`addShapedRoom`). Each is a project of the app's, staged by
 * `tools/build-room-shapes.ts` into `src/state/roomShapes/<id>.xml` — a furnished room with its doors, windows, plinths,
 * parquet and a ceiling lamp — and read with the editor's own parser once chosen; the outlines here draw the pickers.
 */
import type { Level, Scene, Vec2 } from '../core/hp/types';
import { parseProject } from '../core/hp/parseProject';
import { freshUniqueIds } from '../core/hp/uniqueIds';
import { createEmptyScene } from '../core/geometry/edit';
import outlines from './roomShapeOutlines.json';
import { t, type TranslationKey } from '../i18n/I18n';

const shape = <Id extends string>(id: Id, key: TranslationKey) => ({ id, key, get title() { return t(key); } });

/** In the app's order (NewProjectsStorage.ProjectNamesToView), with its names (#RoomShape…). */
export const ROOM_SHAPES = [
  shape('Rectangular', '#RoomShapeRectangular'),
  shape('Corner1', '#RoomShapeCorner1'),
  shape('BayWindow2', '#RoomShapeBayWindow2'),
  shape('Corner2', '#RoomShapeCorner2'),
  shape('Rounded1', '#RoomShapesRounded'),
  shape('Zshape', '#RoomShapeZshape'),
] as const;
export type RoomShapeId = typeof ROOM_SHAPES[number]['id'];

export const roomShapeTitle = (id: RoomShapeId): string => ROOM_SHAPES.find(shape => shape.id === id)!.title;

/** The floor's outline of the shape, on the plan's axes. */
export function roomShapeOutline(id: RoomShapeId): Vec2[] {
  return (outlines as Record<RoomShapeId, [number, number][]>)[id].map(([x, z]) => ({ x, z }));
}

/**
 * The shape's project as the app opens it for a new one (`AddNewRoomList.ProcessClick`). These projects are old and
 * write their paths the Windows way (`Assets\Resources\Textures\floor\wood\parquet_07_01.jpg`); the parser reads them
 * as the app does, so the parquet, the doors' and windows' wood and the lamp resolve as in any project.
 */
export function roomShapeScene(xml: string): Scene {
  const scene = parseProject(xml);
  if (scene.levels[0]?.rooms.length !== 1) throw new Error(t('#GeometryShapeHasNoRoom'));
  return scene;
}

/** The shape's floor, for adding its room to a plan. */
export const roomShapeLevel = (xml: string): Level => roomShapeScene(xml).levels[0];

/**
 * A new project that starts from the shape: its room, 3 m high as the app has it, with the culture and version a new
 * web project starts with, its floors named by place as a new project's are, and every object new, with an id of its own.
 */
export function roomShapeProject(xml: string): Scene {
  const scene = roomShapeScene(xml), empty = createEmptyScene();
  return freshUniqueIds({ ...scene, culture: empty.culture, revision: empty.revision, appVersion: empty.appVersion, warnings: [] });
}
